import React from 'react';
import { Box, Typography, IconButton, Paper, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { acme } from '../../theme/acmeTheme';

const AttachmentList = ({ attachments }) => {
  const handleDownload = (att) => {
    try {
      let blob;

      if (att.content && att.content.data && Array.isArray(att.content.data)) {
        const uint8Array = new Uint8Array(att.content.data);
        blob = new Blob([uint8Array], { type: att.contentType || 'application/octet-stream' });
      } else if (att.content && typeof att.content === 'string') {
        try {
          const binaryString = atob(att.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: att.contentType || 'application/octet-stream' });
        } catch (e) {
          blob = new Blob([att.content], { type: att.contentType || 'application/octet-stream' });
        }
      } else if (att.content instanceof ArrayBuffer || att.content instanceof Uint8Array) {
        blob = new Blob([att.content], { type: att.contentType || 'application/octet-stream' });
      } else {
        blob = new Blob([att.content || ''], { type: att.contentType || 'application/octet-stream' });
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading file. The file might be corrupted.');
    }
  };

  const formatFileSize = (size) => {
    if (!size) return '';
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
    return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  };

  const getFileIcon = (filename) => {
    if (!filename) return <AttachFileIcon />;
    const ext = filename.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'zip':
      case 'rar':
        return '🗜️';
      default:
        return <AttachFileIcon />;
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const chipSx = {
    height: 20,
    fontSize: '0.7rem',
    borderColor: acme.colors.border,
    color: acme.colors.brand,
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          color: acme.colors.brand,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AttachFileIcon fontSize="small" />
        Attachments ({attachments.length})
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attachments.map((att, idx) => (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: acme.radius.md,
              border: `1px solid ${acme.colors.border}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: acme.colors.brand,
                boxShadow: acme.shadow.md,
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                {getFileIcon(att.filename)}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {att.filename || 'Unknown file'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {att.size && (
                    <Chip label={formatFileSize(att.size)} size="small" variant="outlined" sx={chipSx} />
                  )}
                  {att.contentType && (
                    <Chip
                      label={att.contentType.split('/')[1]?.toUpperCase() || 'FILE'}
                      size="small"
                      variant="outlined"
                      sx={chipSx}
                    />
                  )}
                </Box>
              </Box>

              <IconButton
                onClick={() => handleDownload(att)}
                size="small"
                sx={{
                  backgroundColor: acme.colors.brandMuted,
                  color: acme.colors.brand,
                  '&:hover': {
                    backgroundColor: acme.colors.brand,
                    color: acme.colors.brandForeground,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default AttachmentList;
